import { useTranslation } from "react-i18next";

import { Box, Stack, Typography } from "@mui/material";

import { formatEuro } from "../../../../format/formatNumber";
import { SurchargeScope } from "./BalanceSurchargeDialog.types";

interface Props {
  scope: SurchargeScope;
  newTotal: number;
  difference: number;
  /** While false only the affected claims and their current total are shown — there is nothing to project yet. */
  hasPreview: boolean;
}

export function BalanceSurchargePreview({ scope, newTotal, difference, hasPreview }: Props) {
  const { t, i18n } = useTranslation();
  const euro = (amount: number) => formatEuro(amount, i18n.language);
  const signedDifference = `${difference >= 0 ? "+" : "−"} ${euro(Math.abs(difference))}`;

  return (
    <Stack gap={1} sx={{ bgcolor: "grey.50", borderRadius: 1, p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
        {t("claims.surcharge.previewTitle")}
      </Typography>

      <PreviewRow
        label={t("claims.surcharge.affectedCount", { count: scope.affectedCount })}
        value={euro(scope.currentTotal)}
      />

      {hasPreview && (
        <>
          <PreviewRow label={t("claims.surcharge.newTotal")} value={euro(newTotal)} emphasized />
          <PreviewRow label={t("claims.surcharge.difference")} value={signedDifference} highlighted />
        </>
      )}
    </Stack>
  );
}

interface RowProps {
  label: string;
  value: string;
  emphasized?: boolean;
  highlighted?: boolean;
}

function PreviewRow({ label, value, emphasized, highlighted }: RowProps) {
  const color = highlighted ? "warning.main" : undefined;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
      <Typography variant="body2" fontWeight={emphasized ? 600 : undefined} color={color}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={emphasized ? 600 : undefined} color={color} whiteSpace="nowrap">
        {value}
      </Typography>
    </Box>
  );
}
